// Live translation of everything the /app renders. The dictionaries in i18n.js
// cover the chrome instantly; this covers the rest. While a language other
// than English is chosen it watches the page, swaps every piece of visible
// text (text nodes, native placeholders and titles, the labels and
// placeholders of Ionic fields, toast and loading messages) for its
// translation, and asks the backend for the strings it has not met yet, in
// batches. Each string is translated once per language, kept by the backend
// for everyone and in this browser's localStorage, so a page only waits the
// first time anyone sees it. Numbers inside a string become placeholders so
// "3 documents" and "12 documents" share one translation. Choosing English
// puts every original back. Anything under [data-i18n="off"] is left alone.
import { getLanguage, subscribeLanguage } from "@/utils/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT", "PRE", "CODE", "KBD", "svg", "SVG", "CANVAS", "IFRAME", "ION-RIPPLE-EFFECT"]);
const PROP_TAGS = {
  "ION-INPUT": ["label", "placeholder"],
  "ION-TEXTAREA": ["label", "placeholder"],
  "ION-SELECT": ["label", "placeholder"],
  "ION-SEARCHBAR": ["placeholder"],
  "ION-TOAST": ["message", "header"],
  "ION-LOADING": ["message"],
};
const PROP_SELECTOR = Object.keys(PROP_TAGS).map((t) => t.toLowerCase()).join(",");
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const BATCH = 80;

let lang = "en";
let root = null;
let observer = null;
let rescan = null;
let unsubscribe = null;

const mem = new Map();            // lang -> Map(src template -> dst template)
const queue = new Map();          // src template -> [apply(dst template)]
let flushTimer = null;
let inflight = 0;
let saveTimer = null;

const origin = new WeakMap();     // text node -> its English
const applied = new WeakMap();    // text node -> the text we set
const propOrigin = new WeakMap(); // element -> { prop: English }
const propApplied = new WeakMap();// element -> { prop: what we set }
const attrOrigin = new WeakMap(); // element -> { attr: English }
const attrApplied = new WeakMap();// element -> { attr: what we set }
let nudgeFlip = false;

function dict(l) {
  if (!mem.has(l)) {
    let m = new Map();
    try { const raw = localStorage.getItem(`i18n:${l}`); if (raw) m = new Map(Object.entries(JSON.parse(raw))); } catch {}
    mem.set(l, m);
  }
  return mem.get(l);
}
function scheduleSave(l) {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      let m = dict(l);
      if (m.size > 4000) { m = new Map(Array.from(m.entries()).slice(-3000)); mem.set(l, m); }
      localStorage.setItem(`i18n:${l}`, JSON.stringify(Object.fromEntries(m)));
    } catch {}
  }, 800);
}

// "3 of 12 documents" -> "{0} of {1} documents", so one translation serves every count
function mask(text) {
  const nums = [];
  const tpl = text.replace(/\d[\d.,:/%-]*/g, (m) => { nums.push(m); return `{${nums.length - 1}}`; });
  return { tpl, nums };
}
const unmask = (tpl, nums) => tpl.replace(/\{(\d+)\}/g, (m, i) => (nums[+i] !== undefined ? nums[+i] : m));

function translatable(s) {
  const v = String(s || "").trim();
  if (v.length < 2 || v.length > 600) return false;
  if (!/[A-Za-z]/.test(v)) return false;                         // numbers, punctuation, emoji
  if (/\S+@\S+\.\S+/.test(v)) return false;                      // emails
  if (/^(https?:\/\/|www\.)/i.test(v)) return false;             // links
  if (/^[A-Z0-9_#-]{2,14}$/.test(v) && /\d/.test(v)) return false; // codes like EMP-1023
  return true;
}
function skipped(el) {
  for (let p = el; p && p.nodeType === 1; p = p.parentElement) {
    if (SKIP_TAGS.has(p.tagName)) return true;
    if (p.getAttribute && p.getAttribute("data-i18n") === "off") return true;
    if (p.isContentEditable) return true;
  }
  return false;
}

// ── the request pipeline ──
function request(src, apply) {
  const d = dict(lang);
  const hit = d.get(src);
  if (hit !== undefined) { apply(hit); return; }
  let arr = queue.get(src);
  if (!arr) { arr = []; queue.set(src, arr); }
  arr.push(apply);
  schedule();
}
function schedule() { if (!flushTimer) flushTimer = setTimeout(flush, 150); }
async function flush() {
  flushTimer = null;
  if (queue.size === 0) return;
  if (inflight >= 2) { schedule(); return; }
  const l = lang;
  const batch = Array.from(queue.keys()).slice(0, BATCH);
  const entries = batch.map((k) => [k, queue.get(k)]);
  batch.forEach((k) => queue.delete(k));
  inflight++;
  try {
    const res = await fetch(`${BACKEND_URL}/api/translate`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lang: l, texts: batch }),
    });
    const d = await res.json().catch(() => ({}));
    const map = (res.ok && d && d.translations) || {};
    const dic = dict(l);
    for (const [src, fns] of entries) {
      const dst = typeof map[src] === "string" && map[src].trim() ? map[src] : null;
      if (!dst) continue;
      dic.set(src, dst);
      if (l === lang) fns.forEach((fn) => { try { fn(dst); } catch {} });
    }
    scheduleSave(l);
  } catch {} finally {
    inflight--;
    if (queue.size) schedule();
  }
}

// ── text nodes ──
function translateText(node) {
  if (lang === "en" || !node.isConnected) return;
  const parent = node.parentElement;
  if (!parent || skipped(parent)) return;
  // a change React made (not ours) is a new English source
  if (applied.get(node) !== node.data) origin.set(node, node.data);
  const src = origin.get(node);
  if (!translatable(src)) return;
  const { tpl, nums } = mask(src);
  request(tpl, (dstTpl) => {
    if (!node.isConnected || origin.get(node) !== src) return;
    const dst = unmask(dstTpl, nums);
    if (node.data !== dst) { applied.set(node, dst); node.data = dst; }
    nudgeSelect(node);
  });
}
// an ion-select shows its chosen option's text from a render of its own, so
// after that text changes the select is nudged into rendering again
function nudgeSelect(node) {
  const opt = node.parentElement && node.parentElement.closest && node.parentElement.closest("ion-select-option");
  const sel = opt && opt.closest("ion-select");
  if (!sel || sel.compareWith) return;
  nudgeFlip = !nudgeFlip;
  sel.compareWith = nudgeFlip ? (a, b) => a === b : (a, b) => b === a;
  setTimeout(() => { try { sel.compareWith = undefined; } catch {} }, 0);
}

// ── Ionic field labels, toast messages ──
function translateProps(el) {
  if (lang === "en" || skipped(el)) return;
  const props = PROP_TAGS[el.tagName];
  if (!props) return;
  const orig = propOrigin.get(el) || {};
  const done = propApplied.get(el) || {};
  props.forEach((prop) => {
    const cur = el[prop];
    if (typeof cur !== "string") return;
    if (done[prop] !== cur) orig[prop] = cur; // React (or Ionic) set a new English value
    const src = orig[prop];
    if (!translatable(src)) return;
    const { tpl, nums } = mask(src);
    request(tpl, (dstTpl) => {
      if (!el.isConnected || (propOrigin.get(el) || {})[prop] !== src) return;
      const dst = unmask(dstTpl, nums);
      if (el[prop] !== dst) { done[prop] = dst; propApplied.set(el, done); el[prop] = dst; }
    });
  });
  propOrigin.set(el, orig);
  propApplied.set(el, done);
}

// ── native placeholders, titles, aria labels ──
function translateAttrs(el) {
  if (lang === "en" || skipped(el) || !el.getAttribute) return;
  const orig = attrOrigin.get(el) || {};
  const done = attrApplied.get(el) || {};
  ATTRS.forEach((attr) => {
    const cur = el.getAttribute(attr);
    if (cur === null) return;
    if (done[attr] !== cur) orig[attr] = cur;
    const src = orig[attr];
    if (!translatable(src)) return;
    const { tpl, nums } = mask(src);
    request(tpl, (dstTpl) => {
      if (!el.isConnected || (attrOrigin.get(el) || {})[attr] !== src) return;
      const dst = unmask(dstTpl, nums);
      if (el.getAttribute(attr) !== dst) { done[attr] = dst; attrApplied.set(el, done); el.setAttribute(attr, dst); }
    });
  });
  attrOrigin.set(el, orig);
  attrApplied.set(el, done);
}

function walk(node) {
  if (!node) return;
  if (node.nodeType === 3) { translateText(node); return; }
  if (node.nodeType !== 1 && node.nodeType !== 11) return;
  if (node.nodeType === 1) {
    if (skipped(node)) return;
    translateAttrs(node);
    translateProps(node);
  }
  const tw = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === 3) translateText(n);
    else { translateAttrs(n); translateProps(n); }
    n = tw.nextNode();
  }
}

// ── back to English ──
function restoreAll() {
  if (!root) return;
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === 3) {
      if (origin.has(n)) { const src = origin.get(n); if (n.data !== src) n.data = src; origin.delete(n); applied.delete(n); }
    } else {
      const po = propOrigin.get(n);
      if (po) { Object.keys(po).forEach((p) => { try { if (n[p] !== po[p]) n[p] = po[p]; } catch {} }); propOrigin.delete(n); propApplied.delete(n); }
      const ao = attrOrigin.get(n);
      if (ao) { Object.keys(ao).forEach((a) => { if (n.getAttribute(a) !== ao[a]) n.setAttribute(a, ao[a]); }); attrOrigin.delete(n); attrApplied.delete(n); }
    }
    n = tw.nextNode();
  }
}

function onMutations(records) {
  if (lang === "en") return;
  for (const r of records) {
    if (r.type === "childList") r.addedNodes.forEach(walk);
    else if (r.type === "characterData") translateText(r.target);
    else if (r.type === "attributes" && r.target.nodeType === 1) translateAttrs(r.target);
  }
}

function setLang(l) {
  const was = lang;
  lang = l;
  queue.clear();
  if (was !== "en") restoreAll();
  if (lang !== "en") walk(root);
}

// Starts watching and returns the stop. Every /app layout calls this on mount
// and stops it on unmount; the watch is shared and counted, so a page change
// that mounts the next layout before the last one cleans up keeps it running.
let users = 0;
export function startLiveTranslation() {
  if (typeof document === "undefined") return () => {};
  users++;
  if (!observer) {
    root = document.body;
    observer = new MutationObserver(onMutations);
    observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    // Ionic field labels are properties, which no observer reports; sweep them
    rescan = setInterval(() => { if (lang !== "en") root.querySelectorAll(PROP_SELECTOR).forEach(translateProps); }, 1500);
    unsubscribe = subscribeLanguage(setLang);
    setLang(getLanguage());
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    users--;
    if (users > 0) return;
    observer.disconnect(); observer = null;
    clearInterval(rescan); rescan = null;
    unsubscribe?.(); unsubscribe = null;
    if (lang !== "en") restoreAll();
    queue.clear();
    root = null;
  };
}
